import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { calculateFirstPass, money } from './questionnaire2Model'
import { getUserDetails } from '../../utils/AuthUtil'

const format = (value) => money(Math.round(value || 0))
const ease = [0.22, 1, 0.36, 1]

const SCHEDULER_API_BASE = 'https://meet.fabits.com'
const SCHEDULER_EVENT_SLUG = 'wealth-plan-meeting'
const SCHEDULER_USER_SLUG = 'fabitsadheip'
const SCHEDULER_PUBLIC_URL = `${SCHEDULER_API_BASE}/${SCHEDULER_USER_SLUG}/${SCHEDULER_EVENT_SLUG}`

const TIME_BUCKETS = [
  { id: 'morning', label: 'Morning', hint: 'Before noon' },
  { id: 'afternoon', label: 'Afternoon', hint: '12–5 pm' },
  { id: 'evening', label: 'Evening', hint: 'After 5 pm' },
]

const getSlotBucket = (slot) => {
  const hour = new Date(slot.start).getHours()
  if (hour < 12) return 'morning'
  if (hour < 17) return 'afternoon'
  return 'evening'
}

const getFirstTimeBucket = (slots) => {
  const firstSlot = slots?.[0]
  return firstSlot ? getSlotBucket(firstSlot) : ''
}

const toYearMonth = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
const addMonths = (date, months) => new Date(date.getFullYear(), date.getMonth() + months, 1)

const formatDateLabel = (value) =>
  new Intl.DateTimeFormat('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(`${value}T00:00:00`))

const formatMonthLabel = (date) =>
  new Intl.DateTimeFormat('en-IN', {
    month: 'long',
    year: 'numeric',
  }).format(date)

const formatSlotLabel = (start, end) => {
  const formatter = new Intl.DateTimeFormat('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${formatter.format(new Date(start))} – ${formatter.format(new Date(end))}`
}

async function schedulerRequest(path, options) {
  let res
  try {
    res = await fetch(`${SCHEDULER_API_BASE}${path}`, options)
  } catch {
    throw new Error('Could not load live slots right now.')
  }
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.message || 'Could not reach the scheduler')
  return data
}

const formatYears = (years) => {
  if (years === null || !Number.isFinite(years)) return 'Needs monthly surplus'
  if (years <= 0) return 'Already covered'
  if (years < 1) {
    const months = Math.max(1, Math.ceil(years * 12))
    return `${months} ${months === 1 ? 'month' : 'months'}`
  }
  const rounded = Math.round(years * 10) / 10
  return `${rounded.toLocaleString('en-IN', { maximumFractionDigits: 1 })} ${
    rounded === 1 ? 'year' : 'years'
  }`
}

const formatClosureMoment = (estimate) => {
  if (!estimate?.gapClosureMonth || !Number.isFinite(estimate.gapClosureAge)) return ''
  return `${estimate.gapClosureMonth}; you’ll be ${Math.round(estimate.gapClosureAge)} years old`
}

function Amount({ value, delay = 0 }) {
  const ref = useRef(null)
  const reduced = useReducedMotion()
  useEffect(() => {
    if (reduced) return
    let frame
    const start = performance.now() + delay * 1000
    const tick = (now) => {
      const progress = Math.max(0, Math.min(1, (now - start) / 1200))
      if (ref.current) ref.current.textContent = format(value * (1 - (1 - progress) ** 3))
      if (progress < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [value, reduced, delay])
  return (
    <>
      <span className='q2-estimate-sr'>{format(value)}</span>
      <span ref={ref} aria-hidden='true'>
        {format(value)}
      </span>
    </>
  )
}

function Row({ label, value, text }) {
  return (
    <div className='q2-estimate-detail-row'>
      <dt>{label}</dt>
      <dd>{text ?? format(value)}</dd>
    </div>
  )
}

function DetailGroup({ title, children, note }) {
  return (
    <div className='q2-estimate-group'>
      <h3>{title}</h3>
      <dl>{children}</dl>
      {note && <p>{note}</p>}
    </div>
  )
}

function GapGraph({ estimate }) {
  const reduced = useReducedMotion()
  const points = estimate.gapProjection || []
  const maxValue = Math.max(
    ...points.flatMap((point) => [point.requiredAmount, point.investedAmount]),
    1,
  )
  const width = 640
  const height = 260
  const left = 34
  const right = 606
  const top = 42
  const bottom = 210
  const horizon = Math.max(...points.map((point) => point.year), 1)
  const x = (year) => left + (year / horizon) * (right - left)
  const y = (value) => bottom - (Math.min(value, maxValue) / maxValue) * (bottom - top)
  const investedPath = points
    .map(
      (point, index) =>
        `${index ? 'L' : 'M'} ${x(point.year).toFixed(2)} ${y(point.investedAmount).toFixed(2)}`,
    )
    .join(' ')
  const requiredPath = points
    .map(
      (point, index) =>
        `${index ? 'L' : 'M'} ${x(point.year).toFixed(2)} ${y(point.requiredAmount).toFixed(2)}`,
    )
    .join(' ')
  const filledPath = `${investedPath} L ${right} ${bottom} L ${left} ${bottom} Z`
  const finalPoint = points[points.length - 1]
  const closureMoment = formatClosureMoment(estimate)
  return (
    <section className='q2-gap-graph' aria-labelledby='q2-gap-graph-title'>
      <div className='q2-gap-graph-copy'>
        <p id='q2-gap-graph-title'>Gap analysis</p>
        <h2>
          Your surplus catches the inflated gap in{' '}
          <span>{formatYears(estimate.gapClosureYears)}</span>
        </h2>
        <p>
          This plots the required amount growing at {estimate.assumptions.essentialInflation}%
          inflation against the value created by investing your monthly surplus at{' '}
          {estimate.assumptions.gapInvestmentReturn}% yearly return.
          {closureMoment ? ` Around ${closureMoment}.` : ''}
        </p>
      </div>
      <div className='q2-gap-graph-stage' aria-hidden='true'>
        <svg viewBox={`0 0 ${width} ${height}`} role='img'>
          <defs>
            <linearGradient id='q2-gap-fill' x1='0' x2='0' y1='0' y2='1'>
              <stop offset='0%' stopColor='#edff72' stopOpacity='0.44' />
              <stop offset='100%' stopColor='#3214ff' stopOpacity='0.08' />
            </linearGradient>
          </defs>
          <path className='q2-gap-grid-line' d={`M ${left} ${bottom} H ${right}`} />
          <path className='q2-gap-grid-line' d={`M ${left} ${(top + bottom) / 2} H ${right}`} />
          {requiredPath && <path className='q2-gap-required' d={requiredPath} />}
          {investedPath && (
            <>
              <motion.path
                className='q2-gap-area'
                d={filledPath}
                initial={{ opacity: reduced ? 1 : 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: reduced ? 0 : 0.9, delay: reduced ? 0 : 0.15 }}
              />
              <motion.path
                className='q2-gap-invested'
                d={investedPath}
                initial={{ pathLength: reduced ? 1 : 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: reduced ? 0 : 1.35, ease }}
              />
            </>
          )}
          {finalPoint && (
            <circle
              className='q2-gap-end'
              cx={x(finalPoint.year)}
              cy={y(finalPoint.investedAmount)}
              r='5'
            />
          )}
        </svg>
        <div className='q2-gap-legend'>
          <span>
            <i className='is-required' /> Required by then {format(finalPoint?.requiredAmount || 0)}
          </span>
          <span>
            <i className='is-invested' /> Invested surplus {format(finalPoint?.investedAmount || 0)}
          </span>
        </div>
      </div>
    </section>
  )
}

function ExpandableSection({ title, eyebrow, children }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const reduced = useReducedMotion()
  return (
    <section className={`q2-estimate-group-disclosure ${open ? 'is-open' : ''}`}>
      <button
        type='button'
        id={`${id}-trigger`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
      >
        <span>
          <small>{eyebrow}</small>
          {title}
        </span>
        <span className='q2-estimate-toggle' aria-hidden='true'>
          <i />
          <i />
        </span>
      </button>
      <div id={id} role='region' aria-labelledby={`${id}-trigger`}>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key='estimate-group'
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: reduced ? 0 : 0.45, ease }}
              className='q2-estimate-reveal'
            >
              <div className='q2-estimate-nested'>{children}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}

function Disclosure({
  label,
  value,
  textValue,
  index,
  hero = false,
  children,
  hint,
  unit,
  amountDelay,
}) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const reduced = useReducedMotion()
  return (
    <section className={`q2-estimate-disclosure ${hero ? 'is-hero' : ''} ${open ? 'is-open' : ''}`}>
      <h2>
        <button
          type='button'
          id={`${id}-trigger`}
          className='q2-estimate-trigger'
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen(!open)}
        >
          <span className='q2-estimate-index' aria-hidden='true'>
            {index}
          </span>
          <span className='q2-estimate-metric'>
            <span className='q2-estimate-label'>{label}</span>
            <span className='q2-estimate-number'>
              {textValue ?? <Amount value={value} delay={amountDelay} />}
              {unit && <small>{unit}</small>}
            </span>
            <span className='q2-estimate-hint'>{open ? 'Close breakdown' : hint}</span>
          </span>
          <span className='q2-estimate-toggle' aria-hidden='true'>
            <i />
            <i />
          </span>
        </button>
      </h2>
      <div id={id} role='region' aria-labelledby={`${id}-trigger`}>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key='details'
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: reduced ? 0 : 0.4, ease }}
              className='q2-estimate-reveal'
            >
              <div className='q2-estimate-details'>{children}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}

function ScheduleCallModal({ open, planner, onClose, onDashboard }) {
  const reduced = useReducedMotion()
  const userDetails = getUserDetails() || {}
  const self =
    planner.members?.find((member) => member.relationship === 'Self') || planner.members?.[0]
  const defaultName =
    self?.name ||
    `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() ||
    userDetails.name ||
    ''
  const defaultEmail = userDetails.email || ''
  const [monthCursor, setMonthCursor] = useState(() => new Date())
  const [eventDetails, setEventDetails] = useState(null)
  const [availableDates, setAvailableDates] = useState([])
  const [selectedDate, setSelectedDate] = useState('')
  const [slots, setSlots] = useState([])
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [selectedTimeBucket, setSelectedTimeBucket] = useState('')
  const [scheduleStep, setScheduleStep] = useState('date')
  const [form, setForm] = useState({ name: defaultName, email: defaultEmail })
  const [loadingMonth, setLoadingMonth] = useState(false)
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [booking, setBooking] = useState(null)

  useEffect(() => {
    if (!open) return
    setForm({ name: defaultName, email: defaultEmail })
    setMonthCursor(new Date())
    setSelectedDate('')
    setSelectedSlot(null)
    setSelectedTimeBucket('')
    setScheduleStep('date')
    setSlots([])
    setBooking(null)
    setError('')
  }, [defaultEmail, defaultName, open])

  useEffect(() => {
    if (!open) return
    let active = true
    const loadMonth = async () => {
      setLoadingMonth(true)
      setError('')
      try {
        const month = toYearMonth(monthCursor)
        const [details, availability] = await Promise.all([
          schedulerRequest(
            `/api/event-type/${SCHEDULER_EVENT_SLUG}?user=${encodeURIComponent(
              SCHEDULER_USER_SLUG,
            )}`,
          ),
          schedulerRequest(
            `/api/availability/month?event=${SCHEDULER_EVENT_SLUG}&user=${encodeURIComponent(
              SCHEDULER_USER_SLUG,
            )}&month=${month}`,
          ),
        ])
        if (!active) return
        const dates = availability.availableDates || []
        setEventDetails(details)
        setAvailableDates(dates)
        setSelectedDate((current) => (dates.includes(current) ? current : ''))
        setSelectedSlot(null)
        setSelectedTimeBucket('')
        setScheduleStep('date')
      } catch (err) {
        if (active) setError(err.message || 'Could not load available dates.')
      } finally {
        if (active) setLoadingMonth(false)
      }
    }
    loadMonth()
    return () => {
      active = false
    }
  }, [monthCursor, open])

  useEffect(() => {
    if (!open || !selectedDate) return
    let active = true
    const loadSlots = async () => {
      setLoadingSlots(true)
      setSelectedSlot(null)
      setSelectedTimeBucket('')
      setError('')
      try {
        const data = await schedulerRequest(
          `/api/availability?event=${SCHEDULER_EVENT_SLUG}&user=${encodeURIComponent(
            SCHEDULER_USER_SLUG,
          )}&date=${selectedDate}`,
        )
        if (active) {
          const nextSlots = data.slots || []
          setSlots(nextSlots)
          setSelectedTimeBucket(getFirstTimeBucket(nextSlots))
        }
      } catch (err) {
        if (active) setError(err.message || 'Could not load time slots.')
      } finally {
        if (active) setLoadingSlots(false)
      }
    }
    loadSlots()
    return () => {
      active = false
    }
  }, [open, selectedDate])

  if (!open) return null

  const canSubmit = form.name.trim() && form.email.trim() && selectedSlot && !submitting
  const meetingName = eventDetails?.eventType?.name || 'Financial counselling call'
  const meetingDuration = eventDetails?.eventType?.duration || 30
  const selectedDateLabel = selectedDate ? formatDateLabel(selectedDate) : ''
  const selectedSlotLabel = selectedSlot
    ? formatSlotLabel(selectedSlot.start, selectedSlot.end)
    : ''
  const timeBuckets = TIME_BUCKETS.map((bucket) => ({
    ...bucket,
    count: slots.filter((slot) => getSlotBucket(slot) === bucket.id).length,
  })).filter((bucket) => bucket.count > 0)
  const activeTimeBucket = timeBuckets.some((bucket) => bucket.id === selectedTimeBucket)
    ? selectedTimeBucket
    : timeBuckets[0]?.id || ''
  const visibleSlots = activeTimeBucket
    ? slots.filter((slot) => getSlotBucket(slot) === activeTimeBucket)
    : slots
  const goToTimeStep = (date) => {
    if (date !== selectedDate) setSlots([])
    setSelectedDate(date)
    setSelectedSlot(null)
    setSelectedTimeBucket('')
    setScheduleStep('time')
  }

  const confirmBooking = async (event) => {
    event.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setError('')
    try {
      const result = await schedulerRequest('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventSlug: SCHEDULER_EVENT_SLUG,
          userSlug: SCHEDULER_USER_SLUG,
          startTime: selectedSlot.start,
          endTime: selectedSlot.end,
          attendeeName: form.name.trim(),
          attendeeEmail: form.email.trim(),
          notes: 'Financial profile estimate follow-up',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      })
      setBooking(result)
    } catch (err) {
      setError(err.message || 'Could not schedule this call. Please try another time.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        className='q2-schedule-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='q2-schedule-title'
        initial={{ opacity: reduced ? 1 : 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reduced ? 0 : 0.18 }}
      >
        <button type='button' className='q2-schedule-scrim' aria-label='Close' onClick={onClose} />
        <motion.div
          className='q2-schedule-card'
          initial={{ opacity: reduced ? 1 : 0, y: reduced ? 0 : 24, scale: reduced ? 1 : 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 18, scale: 0.98 }}
          transition={{ duration: reduced ? 0 : 0.45, ease }}
        >
          <button type='button' className='q2-schedule-close' onClick={onClose}>
            ×
          </button>
          <div className='q2-schedule-copy'>
            <p>Schedule call</p>
            <h2 id='q2-schedule-title'>{booking ? 'Your call is booked.' : meetingName}</h2>
            <span>{meetingDuration} minutes with the Fabits team.</span>
          </div>

          {booking ? (
            <div className='q2-schedule-success'>
              <div aria-hidden='true'>✓</div>
              <p>
                We’ve saved your slot. You’ll receive the calendar invite and meeting details at{' '}
                <strong>{form.email}</strong>.
              </p>
              <button type='button' className='q2-primary' onClick={onDashboard}>
                Go to dashboard <span aria-hidden='true'>→</span>
              </button>
            </div>
          ) : (
            <form onSubmit={confirmBooking}>
              <div className='q2-schedule-steps' aria-label='Scheduling steps'>
                <button
                  type='button'
                  className={scheduleStep === 'date' ? 'is-active' : selectedDate ? 'is-done' : ''}
                  onClick={() => setScheduleStep('date')}
                >
                  <span>1</span>
                  Pick date
                </button>
                <i aria-hidden='true' />
                <button
                  type='button'
                  className={scheduleStep === 'time' ? 'is-active' : selectedSlot ? 'is-done' : ''}
                  disabled={!selectedDate}
                  onClick={() => selectedDate && setScheduleStep('time')}
                >
                  <span>2</span>
                  Pick time
                </button>
                <i aria-hidden='true' />
                <button type='button' className={selectedSlot ? 'is-active' : ''} disabled>
                  <span>3</span>
                  Confirm
                </button>
              </div>

              <AnimatePresence mode='wait' initial={false}>
                {scheduleStep === 'date' ? (
                  <motion.div
                    key='date-step'
                    initial={{ opacity: reduced ? 1 : 0, x: reduced ? 0 : -18 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: reduced ? 0 : 18 }}
                    transition={{ duration: reduced ? 0 : 0.28, ease }}
                    className='q2-schedule-step-panel'
                  >
                    <div className='q2-schedule-month'>
                      <button
                        type='button'
                        onClick={() => setMonthCursor((current) => addMonths(current, -1))}
                        aria-label='Previous month'
                      >
                        ←
                      </button>
                      <strong>{formatMonthLabel(monthCursor)}</strong>
                      <button
                        type='button'
                        onClick={() => setMonthCursor((current) => addMonths(current, 1))}
                        aria-label='Next month'
                      >
                        →
                      </button>
                    </div>

                    <div className='q2-schedule-section'>
                      <span>Pick a date</span>
                      {loadingMonth ? (
                        <p role='status'>Finding open days…</p>
                      ) : error && !availableDates.length ? (
                        <div className='q2-schedule-fallback'>
                          <p>Live slots are not loading right now.</p>
                          <a href={SCHEDULER_PUBLIC_URL} target='_blank' rel='noreferrer'>
                            Open scheduler
                          </a>
                        </div>
                      ) : availableDates.length ? (
                        <div className='q2-schedule-options q2-schedule-date-grid'>
                          {availableDates.map((date) => (
                            <button
                              key={date}
                              type='button'
                              className={date === selectedDate ? 'is-selected' : ''}
                              onClick={() => goToTimeStep(date)}
                            >
                              {formatDateLabel(date)}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p>No open dates this month. Try the next month.</p>
                      )}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key='time-step'
                    initial={{ opacity: reduced ? 1 : 0, x: reduced ? 0 : 18 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: reduced ? 0 : -18 }}
                    transition={{ duration: reduced ? 0 : 0.28, ease }}
                    className='q2-schedule-step-panel'
                  >
                    <div className='q2-schedule-choice'>
                      <div>
                        <span>Selected date</span>
                        <strong>{selectedDateLabel}</strong>
                      </div>
                      <button type='button' onClick={() => setScheduleStep('date')}>
                        Change
                      </button>
                    </div>

                    <div className='q2-schedule-section'>
                      <span>Pick a time</span>
                      {loadingSlots ? (
                        <p role='status'>Loading times…</p>
                      ) : slots.length ? (
                        <>
                          <div className='q2-schedule-time-buckets' aria-label='Choose time of day'>
                            {timeBuckets.map((bucket) => (
                              <button
                                key={bucket.id}
                                type='button'
                                className={bucket.id === activeTimeBucket ? 'is-selected' : ''}
                                onClick={() => {
                                  setSelectedTimeBucket(bucket.id)
                                  setSelectedSlot(null)
                                }}
                              >
                                <strong>{bucket.label}</strong>
                                <span>
                                  {bucket.count} {bucket.count === 1 ? 'slot' : 'slots'} ·{' '}
                                  {bucket.hint}
                                </span>
                              </button>
                            ))}
                          </div>
                          <div className='q2-schedule-options q2-schedule-slots'>
                            {visibleSlots.map((slot) => (
                              <button
                                key={slot.start}
                                type='button'
                                className={slot.start === selectedSlot?.start ? 'is-selected' : ''}
                                onClick={() => setSelectedSlot(slot)}
                              >
                                {formatSlotLabel(slot.start, slot.end)}
                              </button>
                            ))}
                          </div>
                        </>
                      ) : (
                        <p>No open times on this date. Pick another day.</p>
                      )}
                    </div>

                    <AnimatePresence initial={false}>
                      {selectedSlot && (
                        <motion.div
                          key='confirm-step'
                          initial={{ opacity: reduced ? 1 : 0, y: reduced ? 0 : 14 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: reduced ? 0 : -8 }}
                          transition={{ duration: reduced ? 0 : 0.3, ease }}
                        >
                          <div className='q2-schedule-confirm-strip'>
                            <span>Confirming</span>
                            <strong>
                              {selectedDateLabel}, {selectedSlotLabel}
                            </strong>
                          </div>

                          <div className='q2-schedule-fields'>
                            <label>
                              Name
                              <input
                                value={form.name}
                                onChange={(event) =>
                                  setForm((prev) => ({ ...prev, name: event.target.value }))
                                }
                                autoComplete='name'
                              />
                            </label>
                            <label>
                              Email
                              <input
                                type='email'
                                value={form.email}
                                onChange={(event) =>
                                  setForm((prev) => ({ ...prev, email: event.target.value }))
                                }
                                autoComplete='email'
                              />
                            </label>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </AnimatePresence>

              {error && availableDates.length > 0 && (
                <p className='q2-schedule-error'>
                  {error}{' '}
                  <a href={SCHEDULER_PUBLIC_URL} target='_blank' rel='noreferrer'>
                    Open scheduler
                  </a>
                </p>
              )}
              <button type='submit' className='q2-primary' disabled={!canSubmit}>
                {submitting
                  ? 'Booking your call…'
                  : selectedSlot
                  ? 'Confirm call'
                  : 'Pick a time to continue'}
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default function QuestionnaireEstimate({
  planner,
  origin,
  onBack,
  onReview,
  onDashboard,
  adminMode = false,
}) {
  const heading = useRef(null)
  const future = useRef(null)
  const reduced = useReducedMotion()
  const [arrived, setArrived] = useState(!origin || reduced)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const revealAt = origin && !reduced ? 1.3 : 0
  const estimate = calculateFirstPass(planner)
  const closureMoment = formatClosureMoment(estimate)
  useLayoutEffect(() => {
    heading.current?.closest('main')?.scrollIntoView({ block: 'start', behavior: 'instant' })
    heading.current?.focus({ preventScroll: true })
    if (!origin || reduced || !future.current) {
      setArrived(true)
      return
    }
    const destination = future.current.getBoundingClientRect()
    const animation = future.current.animate(
      [
        {
          transform: `translate(${origin.left - destination.left}px, ${
            origin.top - destination.top
          }px) scale(${origin.width / destination.width}, ${origin.height / destination.height})`,
        },
        { transform: 'translate(0, 0) scale(1, 1)' },
      ],
      { duration: 1150, easing: 'cubic-bezier(.76,0,.24,1)', fill: 'both' },
    )
    let active = true
    animation.finished
      .then(() => {
        if (active) setArrived(true)
      })
      .catch(() => {})
    return () => {
      active = false
      animation.cancel()
    }
  }, [origin, reduced])
  const enter = (delay) => ({
    initial: { opacity: reduced ? 1 : 0, y: reduced ? 0 : 22 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: reduced ? 0 : 1,
      delay: reduced ? 0 : Math.max(0, revealAt + delay),
      ease,
    },
  })
  return (
    <div className='fabits-questionnaire-page q2-page q2-estimate'>
      <div className='q2-estimate-grid' aria-hidden='true' />
      <main className='q2-estimate-main'>
        <motion.header className='q2-estimate-header' {...enter(0.25)}>
          <button type='button' className='q2-estimate-back' onClick={onBack} disabled={!arrived}>
            <span aria-hidden='true'>←</span> Saved profile
          </button>
          <p className='q2-estimate-eyebrow'>Your first estimate</p>
        </motion.header>
        <div className='q2-estimate-intro'>
          <h1 ref={heading} tabIndex={-1} aria-label='A future. In figures.'>
            <motion.span className='q2-estimate-prefix' aria-hidden='true' {...enter(-0.85)}>
              A{' '}
            </motion.span>
            <span className='q2-shared-future' ref={future} aria-hidden='true'>
              future
            </span>
            <motion.span className='q2-estimate-prefix' aria-hidden='true' {...enter(-0.65)}>
              .
            </motion.span>
            <br />
            <motion.span className='q2-estimate-figures' aria-hidden='true' {...enter(-0.3)}>
              In figures.
            </motion.span>
          </h1>
          <motion.p {...enter(0.15)}>
            Your life, your goals, a starting point.
            <br />
            Explore any amount to see what’s behind it.
          </motion.p>
        </div>
        <motion.div {...enter(0.4)}>
          <Disclosure
            hero
            index='01'
            label='Your estimated target today'
            value={estimate.totalRequiredToday}
            hint='Explore your goals & reserves'
            amountDelay={revealAt + 0.4}
          >
            <DetailGroup
              title='The life you’re planning'
              note={`Retirement is funded from age ${estimate.currentAge} through age ${
                estimate.assumptions.planTill - 1
              }, using separate monthly, vacation and annual-cost streams—the same structure as the original planner.`}
            >
              <Row label='Monthly expenses · planned' value={estimate.retirementBasicPlanned} />
              <Row
                label='Monthly expenses · required today'
                value={estimate.retirementBasicCorpus}
              />
              <Row label='Vacation · planned' value={estimate.retirementVacationPlanned} />
              <Row label='Vacation · required today' value={estimate.retirementVacationCorpus} />
              <Row label='Annual expenses · planned' value={estimate.retirementAnnualPlanned} />
              <Row
                label='Annual expenses · required today'
                value={estimate.retirementAnnualCorpus}
              />
              <Row label='Total retirement requirement' value={estimate.retirementCorpus} />
              {estimate.parentCorpus > 0 && (
                <Row label='Parent support' value={estimate.parentCorpus} />
              )}
              {estimate.childCorpus > 0 && (
                <Row label='Children’s education' value={estimate.childCorpus} />
              )}
              {estimate.childWeddingCorpus > 0 && (
                <Row label='Children’s weddings' value={estimate.childWeddingCorpus} />
              )}
            </DetailGroup>
            {estimate.goalFunding.length > 0 && (
              <DetailGroup
                title='Your one-time goals'
                note='Amounts below are the estimated investment needed today for each target year.'
              >
                {estimate.goalFunding.map((goal, index) => (
                  <Row
                    key={index}
                    label={`${goal.name} · ${goal.year}`}
                    value={goal.presentValue}
                  />
                ))}
              </DetailGroup>
            )}
            <DetailGroup
              title='Room for the unexpected'
              note='Emergency reserve uses six months of planned basic spending, rounded to ₹1 lakh. Contingency and replacement each default to 10% of the base requirement, with a ₹50 lakh minimum and ₹25 lakh rounding.'
            >
              <Row label='Emergency reserve' value={estimate.emergencyReserve} />
              <Row label='Contingency reserve' value={estimate.contingencyReserve} />
              <Row label='Asset replacement' value={estimate.assetReplacementReserve} />
            </DetailGroup>
          </Disclosure>
        </motion.div>
        <motion.div {...enter(0.6)}>
          <GapGraph estimate={estimate} />
        </motion.div>
        <motion.div {...enter(0.75)}>
          <ExpandableSection title='Tap to expand the gap analysis' eyebrow='What drives this'>
            <div className='q2-estimate-secondary'>
              <Disclosure
                index='02'
                label='What you’ve already built'
                value={estimate.currentNetWorth}
                hint='Explore your holdings & loans'
                amountDelay={revealAt + 0.7}
              >
                <DetailGroup
                  title='Your current position'
                  note='The original planner uses liquid net worth for the gap: it excludes property, land and gold, which may not be readily available to fund your goals.'
                >
                  <Row label='Total holdings' value={estimate.holdings} />
                  <Row label='Less outstanding loans' value={estimate.loans} />
                  <Row label='Total net worth' value={estimate.totalNetWorth} />
                  <Row label='Less property, land & gold' value={estimate.realAssetValue} />
                  <Row label='Liquid net worth used' value={estimate.liquidNetWorth} />
                </DetailGroup>
              </Disclosure>
              <Disclosure
                index='03'
                label='The distance to your target'
                value={estimate.gapToday}
                hint='See how the gap is calculated'
                amountDelay={revealAt + 0.7}
              >
                <DetailGroup
                  title='Bringing the two together'
                  note='This compares your target with liquid net worth today. Future savings and investment growth are not deducted here.'
                >
                  <Row label='Estimated target today' value={estimate.totalRequiredToday} />
                  <Row label='Less liquid net worth' value={estimate.liquidNetWorth} />
                  <Row label='Remaining gap' value={estimate.gapToday} />
                </DetailGroup>
              </Disclosure>
              <Disclosure
                index='04'
                label='Room in your monthly budget'
                value={estimate.monthlySurplus}
                unit='/ month'
                hint='Explore your income & spending'
                amountDelay={revealAt + 0.7}
              >
                <DetailGroup title='Your monthly cash flow'>
                  <Row label='Income before deductions' value={estimate.monthlyGross} />
                  <Row label='Tax, PF & NPS entered' value={estimate.monthlyDeductions} />
                  <Row label='Income after deductions' value={estimate.monthlyNetIncome} />
                  <Row label='Monthly expenses' value={estimate.monthlyExpenseTotal} />
                  <Row
                    label='Annual costs, spread monthly'
                    value={estimate.annualExpenseTotal / 12}
                  />
                  <Row label='Monthly surplus' value={estimate.monthlySurplus} />
                </DetailGroup>
              </Disclosure>
            </div>
          </ExpandableSection>
        </motion.div>
        <motion.div {...enter(0.95)}>
          <Disclosure
            hero
            index='05'
            label='Estimated time to close the gap'
            textValue={formatYears(estimate.gapClosureYears)}
            hint={closureMoment || 'Based on investing your monthly surplus'}
          >
            <DetailGroup
              title='If this is invested'
              note='This assumes the monthly surplus is invested every month while the required amount keeps rising with inflation.'
            >
              <Row label='Gap today' value={estimate.gapToday} />
              <Row label='Inflated gap by then' value={estimate.futureGapTarget || 0} />
              <Row label='Monthly surplus invested' value={estimate.monthlySurplus} />
              <Row
                label='Inflation on requirement'
                text={`${estimate.assumptions.essentialInflation}%`}
              />
              <Row
                label='Assumed yearly return'
                text={`${estimate.assumptions.gapInvestmentReturn}%`}
              />
              <Row label='Estimated time' text={formatYears(estimate.gapClosureYears)} />
              {closureMoment ? <Row label='When this happens' text={closureMoment} /> : null}
            </DetailGroup>
          </Disclosure>
        </motion.div>
        <motion.footer className='q2-estimate-footer' {...enter(1)}>
          <p className='q2-estimate-footnote'>
            A first estimate, with room to refine. Based on a {estimate.assumptions.hybridReturn}%
            planning return, {estimate.assumptions.essentialInflation}% essential-cost inflation and
            a plan through age {estimate.assumptions.planTill}. The time estimate compares the
            inflation-adjusted requirement with monthly surplus invested at{' '}
            {estimate.assumptions.gapInvestmentReturn}% yearly return. Unanswered amounts are not
            included.
          </p>
          <div className='q2-estimate-actions'>
            {!adminMode && (
              <button type='button' className='q2-primary' onClick={() => setScheduleOpen(true)}>
                Schedule call <span aria-hidden='true'>→</span>
              </button>
            )}
            <button type='button' className='q2-secondary' onClick={onDashboard}>
              {adminMode ? 'Back to First Ideas' : 'Return to dashboard'}
            </button>
            <button type='button' className='q2-text-button' onClick={onReview}>
              Review my answers
            </button>
          </div>
        </motion.footer>
      </main>
      <ScheduleCallModal
        open={scheduleOpen}
        planner={planner}
        onClose={() => setScheduleOpen(false)}
        onDashboard={onDashboard}
      />
    </div>
  )
}
